import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Apple,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Grid2x2,
  Info,
  RefreshCw,
  ShieldCheck,
  Tag,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../../ui/Card';
import Button from '../../ui/Button';
import { Skeleton } from '../../ui/SkeletonLoader';
import { haptics } from '../../../utils/haptics';
import {
  PLATFORMS,
  PLATFORM_LABELS,
  detectArch,
  detectPlatform,
  formatReleaseDate,
  formatSize,
  loadDownloadView,
  recommendDownload,
  splitDownloads,
} from '../../../utils/downloadManifest';
import fallbackManifest from '../../../config/mediaStationManifest.fallback.json';

const DEFAULT_MANIFEST_URL = 'https://downloads.larimax.io/turbosync-media-station/latest.json';
const MANIFEST_URL = import.meta.env.VITE_DOWNLOAD_MANIFEST_URL || DEFAULT_MANIFEST_URL;

const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-elevated-dark-2';
const primaryButtonClasses = `flex items-center justify-center gap-3 w-full px-6 py-3 squircle font-medium text-white bg-gradient-primary shadow-soft hover:shadow-glow transition-shadow touch-target ${focusRing}`;
const secondaryButtonClasses = `flex items-center gap-3 w-full px-4 py-3 squircle-sm text-left bg-gray-100 dark:bg-white/10 dark:border dark:border-white/15 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-white/20 dark:hover:border-white/25 transition-colors touch-target ${focusRing}`;
const textLinkClasses = `inline-flex items-center gap-1 font-medium rounded ${focusRing}`;

const PLATFORM_META = {
  mac: { icon: Apple, hint: 'For the Mac that runs ProPresenter', extension: '.dmg' },
  win: { icon: Grid2x2, hint: 'For the Windows PC that runs ProPresenter', extension: '.exe' },
};

const DOWNLOAD_DESCRIPTIONS = {
  arm64: 'For Macs with an Apple M-series chip',
  x64: 'For Macs with an Intel processor',
  portable: 'Runs without installing',
};

const downloadTitle = (platform, download) => {
  if (download.portable) return 'Portable';
  if (platform === 'mac' && download.archLabel) return download.archLabel;
  return 'Installer';
};

const downloadDescription = (platform, download) => {
  if (download.portable) return DOWNLOAD_DESCRIPTIONS.portable;
  if (platform === 'mac') return DOWNLOAD_DESCRIPTIONS[download.arch] ?? null;
  return null;
};

const readClient = () => {
  const nav = typeof navigator === 'undefined' ? {} : navigator;
  const userAgent = nav.userAgent ?? '';
  const platform = detectPlatform({ platform: nav.userAgentData?.platform ?? nav.platform, userAgent });
  return { platform, arch: detectArch({ platform, userAgent }) };
};

const useClientInfo = () => {
  const [client, setClient] = useState(readClient);

  useEffect(() => {
    const userAgentData = navigator.userAgentData;
    if (!client.platform || typeof userAgentData?.getHighEntropyValues !== 'function') return undefined;
    let active = true;

    const refine = async () => {
      const hints = await userAgentData.getHighEntropyValues(['architecture', 'bitness']).catch(() => null);
      if (!active || !hints) return;
      const arch = detectArch({ platform: client.platform, ...hints, userAgent: navigator.userAgent });
      if (arch) setClient((current) => ({ ...current, arch }));
    };

    refine();
    return () => {
      active = false;
    };
  }, [client.platform]);

  return client;
};

const useDownloadView = () => {
  const [state, setState] = useState({ status: 'loading', view: null });

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const { view } = await loadDownloadView({
          manifestUrl: new URL(MANIFEST_URL, window.location.href).toString(),
          fallback: fallbackManifest,
          fallbackManifestUrl: DEFAULT_MANIFEST_URL,
          signal: controller.signal,
        });
        setState(view ? { status: 'ready', view } : { status: 'error', view: null });
      } catch (error) {
        if (error.name !== 'AbortError') setState({ status: 'error', view: null });
      }
    };

    load();
    return () => controller.abort();
  }, []);

  return state;
};

function CopyHashButton({ download }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(download.sha256);
      haptics.light();
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={`Copy SHA-256 for ${download.name}`}
      className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 squircle-sm text-caption text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors ${focusRing}`}
    >
      {copied ? (
        <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
      ) : (
        <Copy className="w-4 h-4" aria-hidden="true" />
      )}
      <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
    </button>
  );
}

function VerifyDownloads({ downloads }) {
  return (
    <details className="group mt-6 border-t border-gray-200 dark:border-white/10 pt-4">
      <summary
        className={`flex items-center gap-2 cursor-pointer list-none [&::-webkit-details-marker]:hidden text-body-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 rounded ${focusRing}`}
      >
        <ShieldCheck className="w-4 h-4" aria-hidden="true" />
        Verify download (SHA-256)
        <ChevronDown className="w-4 h-4 ml-auto transition-transform group-open:rotate-180" aria-hidden="true" />
      </summary>
      <ul className="mt-3 space-y-3">
        {downloads.map((download) => (
          <li key={download.name} className="p-3 squircle-sm bg-gray-50 dark:bg-white/5">
            <div className="flex items-start justify-between gap-2">
              <p className="text-caption text-gray-600 dark:text-gray-400 break-all">{download.name}</p>
              <CopyHashButton download={download} />
            </div>
            <code className="block mt-1 font-mono text-caption text-gray-800 dark:text-gray-200 break-all" data-testid="download-sha256">
              {download.sha256}
            </code>
          </li>
        ))}
      </ul>
    </details>
  );
}

function PlatformCard({ platform, platformView, preferredName, recommended, showMacHint }) {
  const { icon: PlatformIcon, hint, extension } = PLATFORM_META[platform];
  const label = PLATFORM_LABELS[platform];

  if (!platformView.available) {
    return (
      <Card variant="elevated" data-testid={`download-${platform}`} className="h-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 squircle-sm bg-gray-100 dark:bg-elevated-dark-1">
              <PlatformIcon className="w-6 h-6 text-gray-500 dark:text-gray-400" aria-hidden="true" />
            </div>
            <h2 className="text-heading font-semibold text-gray-900 dark:text-gray-100">{label}</h2>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-body text-gray-600 dark:text-gray-400" data-testid={`download-${platform}-unavailable`}>
            Not available yet for {label}.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { primary, alternatives } = splitDownloads(platformView.downloads, preferredName);
  const primaryTitle = downloadTitle(platform, primary);

  return (
    <Card
      variant="elevated"
      data-testid={`download-${platform}`}
      data-recommended={recommended ? 'true' : undefined}
      aria-labelledby={`download-${platform}-title`}
      role="region"
      className={`h-full flex flex-col ${recommended ? 'ring-2 ring-primary-500/40' : ''}`}
    >
      <CardHeader>
        <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`p-3 squircle-sm ${recommended ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-gray-100 dark:bg-elevated-dark-1'}`}>
              <PlatformIcon
                className={`w-6 h-6 ${recommended ? 'text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'}`}
                aria-hidden="true"
              />
            </div>
            <div>
              <h2 id={`download-${platform}-title`} className="text-heading font-semibold text-gray-900 dark:text-gray-100">
                {label}
              </h2>
              <p className="text-body-sm text-gray-600 dark:text-gray-400 mt-1">{hint}</p>
            </div>
          </div>
          {recommended && (
            <span className="self-start flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-caption bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
              <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
              Recommended for this computer
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col">
        <a
          href={primary.url}
          data-download-link="primary"
          aria-label={`Download ${label} ${primaryTitle}, ${formatSize(primary.sizeBytes)}`}
          className={primaryButtonClasses}
        >
          <Download className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          <span className="flex flex-col items-start leading-tight">
            <span className="text-body">Download {primaryTitle === 'Installer' ? 'installer' : `for ${primaryTitle}`}</span>
            <span className="text-caption text-white/80">
              {extension} · {formatSize(primary.sizeBytes)}
            </span>
          </span>
        </a>
        {downloadDescription(platform, primary) && (
          <p className="mt-2 text-caption text-gray-500 dark:text-gray-400 text-center">{downloadDescription(platform, primary)}</p>
        )}

        {alternatives.length > 0 && (
          <div className="mt-5">
            <p className="text-caption uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Other downloads</p>
            <ul className="space-y-2">
              {alternatives.map((download) => {
                const title = downloadTitle(platform, download);
                return (
                  <li key={download.name}>
                    <a
                      href={download.url}
                      data-download-link="secondary"
                      aria-label={`Download ${label} ${title}, ${formatSize(download.sizeBytes)}`}
                      className={secondaryButtonClasses}
                    >
                      <Download className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-body-sm font-medium">{title}</span>
                        {downloadDescription(platform, download) && (
                          <span className="block text-caption text-gray-500 dark:text-gray-400">{downloadDescription(platform, download)}</span>
                        )}
                      </span>
                      <span className="flex-shrink-0 text-caption text-gray-500 dark:text-gray-400">{formatSize(download.sizeBytes)}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {showMacHint && (
          <p className="mt-4 flex items-start gap-2 text-caption text-gray-600 dark:text-gray-400" data-testid="download-mac-hint">
            <Info className="w-4 h-4 flex-shrink-0 mt-px" aria-hidden="true" />
            <span>
              Not sure which Mac you have? Open the Apple menu and choose About This Mac. A &ldquo;Chip&rdquo; line means Apple Silicon; a
              &ldquo;Processor&rdquo; line that says Intel means Intel.
            </span>
          </p>
        )}

        <div className="mt-auto">
          <VerifyDownloads downloads={platformView.downloads} />
        </div>
      </CardContent>
    </Card>
  );
}

const STEPS = [
  {
    title: 'Install Media Station',
    body: 'Download it above on the computer that runs ProPresenter, then run the installer.',
  },
  {
    title: 'Open it and copy its ID',
    body: 'Open Media Station. Its Media Station ID is shown at the top of the window. Click it to copy.',
  },
  {
    title: 'Link it here',
    body: 'In Media Stations, choose Link Media Station and paste the ID.',
  },
];

function InstallSteps() {
  return (
    <Card variant="elevated" data-testid="download-steps" role="region" aria-labelledby="download-steps-title">
      <CardHeader>
        <h2 id="download-steps-title" className="text-heading font-semibold text-gray-900 dark:text-gray-100">
          Install, then link
        </h2>
      </CardHeader>
      <CardContent>
        <ol className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STEPS.map((step, index) => (
            <li key={step.title} className="flex items-start gap-3">
              <span
                className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-primary text-white text-body-sm font-semibold flex items-center justify-center"
                aria-hidden="true"
              >
                {index + 1}
              </span>
              <div>
                <h3 className="text-body font-semibold text-gray-900 dark:text-gray-100">{step.title}</h3>
                <p className="text-body-sm text-gray-600 dark:text-gray-400 mt-1">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
        <Link
          to="/media-stations"
          onClick={() => haptics.light()}
          data-testid="download-steps-stations-link"
          className={`${textLinkClasses} mt-5 text-body-sm text-primary-600 dark:text-primary-400 hover:underline`}
        >
          Go to Media Stations
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </CardContent>
    </Card>
  );
}

function ReleaseInfo({ view, timeZone }) {
  const released = formatReleaseDate(view.builtAtUtc, timeZone);
  const pill = 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-caption bg-white/70 dark:bg-white/10 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-white/10';
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={pill} data-testid="download-version">
        <Tag className="w-3.5 h-3.5" aria-hidden="true" />
        Version {view.version}
      </span>
      {released && (
        <span className={pill}>
          <CalendarDays className="w-3.5 h-3.5" aria-hidden="true" />
          Released {released}
        </span>
      )}
    </div>
  );
}

export function DownloadView({ view, client, timeZone }) {
  const detected = client.platform && view.platforms[client.platform]?.available ? client.platform : null;
  const ordered = detected ? [detected, ...PLATFORMS.filter((platform) => platform !== detected)] : PLATFORMS;

  return (
    <div className="space-y-6">
      <ReleaseInfo view={view} timeZone={timeZone} />
      {!detected && (
        <p className="flex items-start gap-2 text-body-sm text-gray-600 dark:text-gray-400" data-testid="download-other-device">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          Media Station runs on macOS and Windows. Download it on the computer that runs ProPresenter.
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {ordered.map((platform) => {
          const isClient = platform === detected;
          const recommendation = recommendDownload(view, { platform, arch: isClient ? client.arch : null });
          return (
            <PlatformCard
              key={platform}
              platform={platform}
              platformView={view.platforms[platform]}
              preferredName={recommendation?.name}
              recommended={isClient}
              showMacHint={platform === 'mac' && !(isClient && recommendation?.archMatched)}
            />
          );
        })}
      </div>
      <InstallSteps />
    </div>
  );
}

function DownloadSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-busy="true" aria-label="Loading downloads" data-testid="download-loading">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PLATFORMS.map((platform) => (
          <div key={platform} className="bg-white dark:bg-elevated-dark-2 squircle p-6 shadow-soft">
            <div className="flex items-center gap-3 mb-6">
              <Skeleton className="w-12 h-12" />
              <div className="flex-1">
                <Skeleton variant="text" className="w-1/3 h-5 mb-2" />
                <Skeleton variant="text" className="w-2/3 h-4" />
              </div>
            </div>
            <Skeleton className="w-full h-14 mb-4" />
            <Skeleton className="w-full h-12" />
          </div>
        ))}
      </div>
      <Skeleton className="w-full h-40" />
    </div>
  );
}

function DownloadError() {
  return (
    <Card variant="elevated" data-testid="download-error" role="alert">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="p-3 squircle-sm bg-red-100 dark:bg-red-900/30 self-start">
          <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h2 className="text-heading font-semibold text-gray-900 dark:text-gray-100">Downloads are unavailable</h2>
          <p className="text-body-sm text-gray-600 dark:text-gray-400 mt-1">
            We couldn&apos;t load the download information. Please try again in a few minutes.
          </p>
        </div>
        <Button variant="secondary" onClick={() => window.location.reload()}>
          <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
          Try again
        </Button>
      </div>
    </Card>
  );
}

export default function DownloadPage() {
  const { status, view } = useDownloadView();
  const client = useClientInfo();

  return (
    <div className="min-h-screen pb-4 safe-top">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <Link
            to="/media-stations"
            data-testid="download-back-link"
            className={`${textLinkClasses} mb-4 text-body-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100`}
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" />
            Media Stations
          </Link>
          <h1 className="text-display text-gray-900 dark:text-gray-100 mb-2">Download Media Station</h1>
          <p className="text-body text-gray-600 dark:text-gray-400">
            Install Media Station on the computer that runs ProPresenter, then link it from Media Stations.
          </p>
        </div>

        {status === 'loading' && <DownloadSkeleton />}
        {status === 'error' && <DownloadError />}
        {status === 'ready' && <DownloadView view={view} client={client} />}
      </div>
    </div>
  );
}
